import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// The X edge functions live on the same Supabase project as the rest of the app.
const X_FUNCTIONS_BASE = import.meta.env.VITE_SUPABASE_URL;

interface XConnectionStatus {
  connected: boolean;
  username?: string;
  x_user_id?: string;
  token_expires_at?: string;
  last_sync?: {
    sync_type: string;
    tweets_fetched: number;
    tweets_inserted: number;
    started_at: string;
    completed_at: string | null;
    error: string | null;
  } | null;
}

/**
 * Headers for calling the X functions as the signed-in user. The functions
 * identify the user from this access token, never from the request body.
 */
async function authHeaders(): Promise<Record<string, string> | null> {
  const { data } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;
  if (!accessToken) return null;
  return {
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    Authorization: `Bearer ${accessToken}`,
  };
}

export function useXBookmarks() {
  const { user } = useAuth();
  const [status, setStatus] = useState<XConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const headers = await authHeaders();
      if (!headers) return;
      const res = await fetch(
        `${X_FUNCTIONS_BASE}/functions/v1/x-auth?action=status`,
        { headers }
      );

      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch X status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const connect = useCallback(async () => {
    if (!user) return;
    try {
      const headers = await authHeaders();
      if (!headers) {
        toast.error('Not authenticated. Please sign in again.');
        return;
      }
      // x-auth returns the X consent URL; app_redirect brings the callback back here
      const appRedirect = encodeURIComponent(window.location.origin);
      const res = await fetch(
        `${X_FUNCTIONS_BASE}/functions/v1/x-auth?action=authorize&app_redirect=${appRedirect}`,
        { headers }
      );
      const data = await res.json();
      if (!res.ok || !data.url) {
        toast.error(data.error || 'Failed to start X connection');
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error('Failed to start X connection');
    }
  }, [user]);

  const disconnect = useCallback(async () => {
    if (!user) return;
    try {
      const headers = await authHeaders();
      if (!headers) return;
      const res = await fetch(
        `${X_FUNCTIONS_BASE}/functions/v1/x-auth?action=disconnect`,
        { method: 'POST', headers }
      );

      if (res.ok) {
        setStatus({ connected: false });
        toast.success('X account disconnected');
      }
    } catch (err) {
      toast.error('Failed to disconnect X account');
    }
  }, [user]);

  const syncNow = useCallback(
    async (mode: 'test' | 'backfill' | 'daily' = 'backfill', backfillMonths: number = 6) => {
      if (!user) return;
      setIsSyncing(true);
      try {
        const headers = await authHeaders();
        if (!headers) {
          toast.error('Not authenticated. Please sign in again.');
          return;
        }

        const res = await fetch(
          `${X_FUNCTIONS_BASE}/functions/v1/x-bookmarks-sync`,
          {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ mode, backfill_months: backfillMonths }),
          }
        );

        if (res.ok) {
          const data = await res.json();
          toast.success(
            `Synced ${data.tweets_inserted} new tweet${data.tweets_inserted !== 1 ? 's' : ''}`
          );
          await fetchStatus();
        } else {
          const err = await res.json();
          toast.error(err.error || 'Sync failed');
        }
      } catch (err) {
        toast.error('Sync failed: ' + (err as Error).message);
      } finally {
        setIsSyncing(false);
      }
    },
    [user, fetchStatus]
  );

  return {
    status,
    isLoading,
    isSyncing,
    fetchStatus,
    connect,
    disconnect,
    syncNow,
  };
}
